import {useMemo, useState} from 'react'
import {Button, Spinner, Form, Row, Col, Badge} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import Select from 'react-select'
import {BULK_ASSIGN_PERMISSIONS, PERMISSION_USERS} from '../../../../gql/authz'
import {parseId} from '../../../../helpers'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'

const M = defineMessages({
  step1: {id: 'admin.bulk.step1', defaultMessage: '1. Pick permissions'},
  step2: {id: 'admin.bulk.step2', defaultMessage: '2. Apply to'},
  step3: {id: 'admin.bulk.step3', defaultMessage: '3. Effect'},
  tabRoles: {id: 'admin.bulk.tab.roles', defaultMessage: 'Roles'},
  tabGroups: {id: 'admin.bulk.tab.groups', defaultMessage: 'Groups'},
  tabUsers: {id: 'common.field.users', defaultMessage: 'Users'},
  searchUsers: {id: 'admin.bulk.searchUsers', defaultMessage: 'Type to search users…'},
  effectAllow: {id: 'admin.bulk.effect.allow', defaultMessage: 'Allow (grant override)'},
  effectDeny: {id: 'admin.bulk.effect.deny', defaultMessage: 'Deny (block override)'},
  effectClear: {id: 'admin.bulk.effect.clear', defaultMessage: 'Clear (inherit)'},
  effectGrant: {id: 'admin.bulk.effect.grant', defaultMessage: 'Grant'},
  effectRevoke: {id: 'admin.bulk.effect.revoke', defaultMessage: 'Revoke'},
  applyButton: {
    id: 'admin.bulk.action.apply',
    defaultMessage: 'Apply to {count, plural, one {# target} other {# targets}}',
  },
  hintRoleGroup: {
    id: 'admin.bulk.hint.roleGroup',
    defaultMessage: 'Grant adds the permission; Revoke removes it.',
  },
  hintUser: {
    id: 'admin.bulk.hint.user',
    defaultMessage: 'Allow/Deny set a per-user override; Clear returns to inherited.',
  },
  hintRefresh: {
    id: 'admin.bulk.hint.refresh',
    defaultMessage: 'Changes take effect on the next permission refresh.',
  },
  // El confirm se arma con los conteos ya formateados aparte: dos plurales en
  // un mismo mensaje los prohibe `no-multiple-plurals`.
  confirm: {id: 'admin.bulk.confirm', defaultMessage: '{verb} {permissions} on {targets}?'},
  permissionCount: {
    id: 'admin.bulk.permissionCount',
    defaultMessage: '{count, plural, one {# permission} other {# permissions}}',
  },
  targetsRole: {id: 'admin.bulk.targets.role', defaultMessage: '{count, plural, one {# role} other {# roles}}'},
  targetsGroup: {id: 'admin.bulk.targets.group', defaultMessage: '{count, plural, one {# group} other {# groups}}'},
  targetsUser: {id: 'admin.bulk.targets.user', defaultMessage: '{count, plural, one {# user} other {# users}}'},
  verbGrant: {id: 'admin.bulk.verb.grant', defaultMessage: 'Grant'},
  verbAllow: {id: 'admin.bulk.verb.allow', defaultMessage: 'Allow'},
  verbDeny: {id: 'admin.bulk.verb.deny', defaultMessage: 'Deny'},
  verbClear: {id: 'admin.bulk.verb.clear', defaultMessage: 'Clear'},
  toastApplied: {id: 'admin.bulk.toast.applied', defaultMessage: 'Applied to {targets}.'},
  toastNotSaved: {
    id: 'admin.bulk.toast.notSaved',
    defaultMessage: 'The permission assignment was not saved',
  },
  toastFailed: {id: 'admin.bulk.toast.failed', defaultMessage: 'Bulk assignment failed'},
  targetWithCount: {id: 'admin.bulk.targetWithCount', defaultMessage: '{name} ({count})'},
})

export type Perm = {codename: string; label: string; description: string}
export type PermModule = {key: string; title: string; permissions: Perm[]}
export type Target = {id: string; name: string; member_count: number; permissions: string[]}

type TargetType = 'role' | 'group' | 'user'

// Bulk assignment: pick permissions (by module), then apply to many roles /
// groups / users at once.
export const BulkAssignPanel = ({
  modules,
  roles,
  groups,
  onDone,
}: {
  modules: PermModule[]
  roles: Target[]
  groups: Target[]
  onDone: () => void
}) => {
  const intl = useIntl()
  const [bulkAssign, {loading: applying}] = useMutation(BULK_ASSIGN_PERMISSIONS)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [targetType, setTargetType] = useState<TargetType>('role')
  const [checkedTargets, setCheckedTargets] = useState<Set<string>>(new Set())
  const [userTargets, setUserTargets] = useState<{value: string; label: string}[]>([])
  const [effect, setEffect] = useState<'allow' | 'deny' | 'clear'>('allow')
  const [userSearch, setUserSearch] = useState('')

  const {data: usersData} = useQuery(PERMISSION_USERS, {
    variables: {first: 50, isActive: true, name: userSearch || null},
    skip: targetType !== 'user',
    fetchPolicy: 'cache-and-network',
  })
  const userOptions = useMemo(
    () =>
      (usersData?.allUsers?.edges ?? []).map((e: any) => {
        const name = `${e.node.firstName || ''} ${e.node.lastName || ''}`.trim()
        return {
          value: String(parseId(e.node.id)),
          label: name ? `${name} — ${e.node.email}` : e.node.email,
        }
      }),
    [usersData]
  )

  const togglePerm = (cn: string) =>
    setPicked((prev) => {
      const n = new Set(prev)
      n.has(cn) ? n.delete(cn) : n.add(cn)
      return n
    })
  const toggleModulePerms = (mod: PermModule, on: boolean) =>
    setPicked((prev) => {
      const n = new Set(prev)
      mod.permissions.forEach((p) => (on ? n.add(p.codename) : n.delete(p.codename)))
      return n
    })
  const toggleTarget = (id: string) =>
    setCheckedTargets((prev) => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })

  const targetIds =
    targetType === 'user' ? userTargets.map((u) => u.value) : Array.from(checkedTargets)

  // For roles/groups the effect is grant/revoke (allow/clear). For users it's allow/deny/clear.
  const effectOptions =
    targetType === 'user'
      ? [
          {v: 'allow', l: intl.formatMessage(M.effectAllow)},
          {v: 'deny', l: intl.formatMessage(M.effectDeny)},
          {v: 'clear', l: intl.formatMessage(M.effectClear)},
        ]
      : [
          {v: 'allow', l: intl.formatMessage(M.effectGrant)},
          {v: 'clear', l: intl.formatMessage(M.effectRevoke)},
        ]

  const setType = (t: TargetType) => {
    setTargetType(t)
    setCheckedTargets(new Set())
    setUserTargets([])
    if (t !== 'user' && effect === 'deny') setEffect('allow')
  }

  const canApply = picked.size > 0 && targetIds.length > 0 && !applying

  const handleApply = async () => {
    if (!canApply) return
    const targetsMsg =
      targetType === 'user' ? M.targetsUser : targetType === 'group' ? M.targetsGroup : M.targetsRole
    const formatTargets = (count: number) => intl.formatMessage(targetsMsg, {count})
    const verbMsg =
      effect === 'clear'
        ? M.verbClear
        : effect === 'deny'
          ? M.verbDeny
          : targetType === 'user'
            ? M.verbAllow
            : M.verbGrant
    const confirmText = intl.formatMessage(M.confirm, {
      verb: intl.formatMessage(verbMsg),
      permissions: intl.formatMessage(M.permissionCount, {count: picked.size}),
      targets: formatTargets(targetIds.length),
    })
    if (!window.confirm(confirmText)) return
    try {
      const {data} = await bulkAssign({
        variables: {codenames: Array.from(picked), effect, targetType, targetIds},
      })
      const res = data?.bulkAssignPermissions
      if (!res?.success) throw new Error(intl.formatMessage(M.toastNotSaved))
      toast.success(intl.formatMessage(M.toastApplied, {targets: formatTargets(res.updated)}))
      setPicked(new Set())
      setCheckedTargets(new Set())
      setUserTargets([])
      onDone()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastFailed))
    }
  }

  return (
    <Row className='g-4'>
      {/* 1. Permissions */}
      <Col lg={7}>
        <div className='fw-bold mb-2'>
          <FormattedMessage {...M.step1} /> <Badge bg='light' text='dark'>{picked.size}</Badge>
        </div>
        <div className='border rounded p-3' style={{maxHeight: '60vh', overflow: 'auto'}}>
          {modules.map((mod) => {
            const all = mod.permissions.every((p) => picked.has(p.codename))
            return (
              <div key={mod.key} className='mb-3'>
                <Form.Check
                  type='checkbox'
                  id={`bulk-mod-${mod.key}`}
                  className='fw-bolder text-uppercase fs-8 text-gray-700'
                  label={mod.title}
                  checked={all}
                  onChange={(e) => toggleModulePerms(mod, e.target.checked)}
                />
                <div className='ms-4'>
                  {mod.permissions.map((p) => (
                    <Form.Check
                      key={p.codename}
                      type='checkbox'
                      id={`bulk-${p.codename}`}
                      label={<span>{p.label} <code className='text-muted fs-9'>{p.codename}</code></span>}
                      checked={picked.has(p.codename)}
                      onChange={() => togglePerm(p.codename)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </Col>

      {/* 2. Targets + 3. Apply */}
      <Col lg={5}>
        <div className='fw-bold mb-2'>
          <FormattedMessage {...M.step2} />
        </div>
        <div className='btn-group btn-group-sm mb-3' role='group'>
          {(['role', 'group', 'user'] as TargetType[]).map((t) => (
            <button
              key={t}
              type='button'
              className={`btn btn-${targetType === t ? 'primary' : 'light'}`}
              onClick={() => setType(t)}
            >
              {t === 'role' ? (
                <FormattedMessage {...M.tabRoles} />
              ) : t === 'group' ? (
                <FormattedMessage {...M.tabGroups} />
              ) : (
                <FormattedMessage {...M.tabUsers} />
              )}
            </button>
          ))}
        </div>

        {targetType === 'user' ? (
          <Select
            isMulti
            options={userOptions}
            value={userTargets}
            onChange={(v: any) => setUserTargets(v || [])}
            onInputChange={(v: string) => setUserSearch(v)}
            filterOption={() => true}
            placeholder={intl.formatMessage(M.searchUsers)}
            classNamePrefix='rs'
          />
        ) : (
          <div className='border rounded p-3' style={{maxHeight: '38vh', overflow: 'auto'}}>
            {(targetType === 'role' ? roles : groups).map((t) => (
              <Form.Check
                key={t.id}
                type='checkbox'
                id={`bulk-target-${targetType}-${t.id}`}
                label={intl.formatMessage(M.targetWithCount, {name: t.name, count: t.member_count})}
                checked={checkedTargets.has(t.id)}
                onChange={() => toggleTarget(t.id)}
              />
            ))}
          </div>
        )}

        <div className='fw-bold mt-4 mb-2'>
          <FormattedMessage {...M.step3} />
        </div>
        <Form.Select size='sm' value={effect} onChange={(e) => setEffect(e.target.value as any)}>
          {effectOptions.map((o) => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </Form.Select>

        <Button className='mt-4 w-100' variant='primary' onClick={handleApply} disabled={!canApply}>
          {applying ? (
            <Spinner animation='border' size='sm' />
          ) : (
            <FormattedMessage {...M.applyButton} values={{count: targetIds.length || 0}} />
          )}
        </Button>
        <div className='text-muted fs-8 mt-2'>
          {targetType !== 'user' && <FormattedMessage {...M.hintRoleGroup} />}
          {targetType === 'user' && <FormattedMessage {...M.hintUser} />} <FormattedMessage {...M.hintRefresh} />
        </div>
      </Col>
    </Row>
  )
}
